import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Slider,
  MenuItem,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PhotoAlbum as PhotoAlbumIcon,
  AutoAwesome as AutoAwesomeIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Image as ImageIcon,
  People as PeopleIcon,
  Category as CategoryIcon,
  Timeline as TimelineIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import albumService from '../api/albumService';
import sessionService from '../api/sessionService';
import AuthenticatedImage from '../components/AuthenticatedImage';

const AlbumCreationPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [albumData, setAlbumData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [loadingArtifacts, setLoadingArtifacts] = useState(false);
  const [error, setError] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [generationArtifacts, setGenerationArtifacts] = useState(null);
  
  // Album configuration
  const [config, setConfig] = useState({
    totalPages: 30,
    imagesPerPage: {
      single: 5,
      double: 8,
      quad: 10,
      multi: 7
    },
    userContext: '',
    albumTheme: '',
    prioritizeTimeline: true,
    prioritizePeople: true,
    prioritizeClusters: true,
    callModel: false,
    aiModel: 'gpt-6.1-sol',
    maxPhotosInPrompt: 30,
    clusterSampleSize: 2,
    useClusterSamplingEvenWhenUnlimited: false,
    compactAiDescriptionInPrompt: true,
    aiDescriptionMaxChars: 260
  });

  const [availableModels, setAvailableModels] = useState([]);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const detailedAlbumData = recommendations?.albumData || albumData;

  useEffect(() => {
    loadData();
  }, [sessionId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    setRecommendations(null);
    setGenerationArtifacts(null);
    try {
      // Load supported AI models
      try {
        const modelsData = await albumService.getSupportedAiModels();
        if (modelsData?.models) {
          setAvailableModels(modelsData.models);
        }
      } catch (modelsErr) {
        console.warn('Failed to load AI models list:', modelsErr.message);
      }

      // Load session info
      const sessionData = await sessionService.getSession(sessionId);
      setSession(sessionData);

      const lastAlbumConfig = sessionData?.albumGeneration?.lastAlbumConfiguration;
      if (lastAlbumConfig && typeof lastAlbumConfig === 'object') {
        setConfig(prev => ({
          ...prev,
          ...lastAlbumConfig,
          imagesPerPage: {
            ...prev.imagesPerPage,
            ...(lastAlbumConfig.imagesPerPage || {})
          }
        }));
      }

      // Load album data
      const albumDataResponse = await albumService.getAlbumData(sessionId);
      setAlbumData(albumDataResponse);

      const lastRecommendations = sessionData?.albumGeneration?.lastRecommendations;
      if (lastRecommendations) {
        setRecommendations({
          success: true,
          sessionId,
          albumConfiguration: sessionData?.albumGeneration?.lastAlbumConfiguration || null,
          recommendations: lastRecommendations,
          aiModel: sessionData?.albumGeneration?.lastModel || 'Unknown',
          tokensUsed: sessionData?.albumGeneration?.lastTokenUsage?.total_tokens || 0,
          tokenUsage: sessionData?.albumGeneration?.lastTokenUsage || null,
          tokenEstimation: sessionData?.albumGeneration?.lastTokenEstimation || null,
          actualCostEstimationUSD: sessionData?.albumGeneration?.lastActualCostEstimationUSD || null,
          modelResponseTimeMs: sessionData?.albumGeneration?.lastModelResponseTimeMs ?? null,
          totalRequestTimeMs: sessionData?.albumGeneration?.lastTotalRequestTimeMs ?? null,
          promptFilePath: sessionData?.albumGeneration?.lastPromptFilePath || null,
          apiPromptFilePath: sessionData?.albumGeneration?.lastApiPromptFilePath || null,
          responseFilePath: sessionData?.albumGeneration?.lastResponseFilePath || null,
          generatedAt: sessionData?.albumGeneration?.lastGeneratedAt
            ? new Date(sessionData.albumGeneration.lastGeneratedAt).toISOString()
            : null,
          loadedFromSession: true,
          storedApiPromptTextTruncated: Boolean(sessionData?.albumGeneration?.lastApiPromptTextTruncated),
          storedModelResponseTextTruncated: Boolean(sessionData?.albumGeneration?.lastModelResponseTextTruncated)
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to load album data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAlbum = async () => {
    setGenerating(true);
    setError(null);
    try {
      const result = await albumService.generateAlbumRecommendations(sessionId, config);
      setRecommendations(result);
    } catch (err) {
      setError(err.message || 'Failed to generate album recommendations');
    } finally {
      setGenerating(false);
    }
  };

  const handleLoadGenerationArtifacts = async () => {
    setLoadingArtifacts(true);
    setError(null);
    try {
      const result = await albumService.getAlbumGenerationArtifacts(sessionId);
      setGenerationArtifacts(result);
    } catch (err) {
      setError(err.message || 'Failed to load persisted prompt/response from DB');
    } finally {
      setLoadingArtifacts(false);
    }
  };

  const handleConfigChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImagesPerPageChange = (layout, value) => {
    setConfig(prev => ({
      ...prev,
      imagesPerPage: {
        ...prev.imagesPerPage,
        [layout]: value
      }
    }));
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>Loading album data...</Typography>
      </Container>
    );
  }

  if (error && !albumData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/sessions')}
          sx={{ mt: 2 }}
        >
          Back to Sessions
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={() => navigate('/sessions')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            <PhotoAlbumIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Create Album
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {session?.name}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => setConfigDialogOpen(true)}
        >
          Configure Album
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {albumData && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ImageIcon color="primary" />
                  <Box>
                    <Typography variant="h4">{albumData.stats?.totalImages}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Photos</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CategoryIcon color="secondary" />
                  <Box>
                    <Typography variant="h4">{albumData.stats.totalClusters}</Typography>
                    <Typography variant="body2" color="text.secondary">Clusters</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PeopleIcon color="success" />
                  <Box>
                    <Typography variant="h4">{albumData.people.length}</Typography>
                    <Typography variant="body2" color="text.secondary">People</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TimelineIcon color="info" />
                  <Box>
                    <Typography variant="h4">{albumData.stats.imagesWithGPS}</Typography>
                    <Typography variant="body2" color="text.secondary">With GPS</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Data Summary */}
      {albumData && !recommendations && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Session Data Summary</Typography>
            <Divider sx={{ mb: 2 }} />
            
            {/* Clusters */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">
                  <CategoryIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                  Clusters ({albumData.clusters.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {albumData.clusters.map(cluster => (
                    <Chip
                      key={cluster.id}
                      label={`${cluster.label}: ${cluster.imageIds.length} photos`}
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* People */}
            {albumData.people.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">
                    <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                    People Detected ({albumData.people.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {albumData.people.map(person => (
                      <Chip key={person} label={person} color="primary" size="small" />
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Date Range */}
            {albumData.stats.dateRange.earliest && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">
                    <TimelineIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                    Timeline
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    <strong>Earliest:</strong> {new Date(albumData.stats.dateRange.earliest).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Latest:</strong> {new Date(albumData.stats.dateRange.latest).toLocaleDateString()}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            )}
          </CardContent>
        </Card>
      )}

      {/* User Input Section */}
      {!recommendations && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Album Context</Typography>
            <Divider sx={{ mb: 3 }} />
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Tell us about this album"
              placeholder="Describe the event, occasion, or any specific preferences for the album. For example: 'This is a wedding album. Focus on the ceremony and reception. Prioritize photos with the bride and groom.'"
              value={config.userContext}
              onChange={(e) => handleConfigChange('userContext', e.target.value)}
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="Album Theme (optional)"
              placeholder="e.g., Elegant Wedding, Summer Adventure, Family Reunion"
              value={config.albumTheme}
              onChange={(e) => handleConfigChange('albumTheme', e.target.value)}
              sx={{ mb: 3 }}
            />

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Album Priorities</Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.prioritizeTimeline}
                    onChange={(e) => handleConfigChange('prioritizeTimeline', e.target.checked)}
                  />
                }
                label="Maintain chronological timeline"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.prioritizePeople}
                    onChange={(e) => handleConfigChange('prioritizePeople', e.target.checked)}
                  />
                }
                label="Group photos by people"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.prioritizeClusters}
                    onChange={(e) => handleConfigChange('prioritizeClusters', e.target.checked)}
                  />
                }
                label="Use clustering data for similar scenes"
              />

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>AI Execution</Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.callModel}
                    onChange={(e) => handleConfigChange('callModel', e.target.checked)}
                  />
                }
                label="Call AI model (unchecked = prompt inspection mode)"
              />

              <TextField
                select
                fullWidth
                size="small"
                label="AI Model"
                value={config.aiModel}
                onChange={(e) => handleConfigChange('aiModel', e.target.value)}
                sx={{ mt: 1 }}
                helperText={
                  availableModels.find(m => m.id === config.aiModel && !m.available)
                    ? '⚠️ API key not configured for this model'
                    : 'Select which AI model to use for album generation'
                }
              >
                {availableModels.length > 0
                  ? availableModels.map((m) => (
                      <MenuItem key={m.id} value={m.id} disabled={!m.available}>
                        {m.label} ({m.provider}){!m.available ? ' — not configured' : ''}
                      </MenuItem>
                    ))
                  : [
                      <MenuItem key="gpt-6.1-sol" value="gpt-6.1-sol">GPT-6.1 Sol (openai)</MenuItem>,
                      <MenuItem key="claude-opus-4-6" value="claude-opus-4-6">Claude Opus 4.6 (anthropic)</MenuItem>
                    ]
                }
              </TextField>

              <TextField
                select
                fullWidth
                size="small"
                label="Max photos to include in prompt"
                value={config.maxPhotosInPrompt}
                onChange={(e) => {
                  const value = e.target.value;
                  handleConfigChange('maxPhotosInPrompt', value === 'all' ? 'all' : Number(value));
                }}
                sx={{ mt: 1 }}
                helperText="Lower numbers = fewer tokens"
              >
                <MenuItem value="all">All photos</MenuItem>
                {[10, 20, 30, 50, 75, 100].map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </TextField>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.useClusterSamplingEvenWhenUnlimited}
                    onChange={(e) => handleConfigChange('useClusterSamplingEvenWhenUnlimited', e.target.checked)}
                    disabled={config.maxPhotosInPrompt !== 'all'}
                  />
                }
                label="Use cluster sampling even when unlimited (will NOT send all photos)"
                sx={{ mt: 1 }}
              />

              <TextField
                select
                fullWidth
                size="small"
                label="Photos per cluster in prompt (diverse sampling)"
                value={config.clusterSampleSize}
                onChange={(e) => handleConfigChange('clusterSampleSize', Number(e.target.value))}
                sx={{ mt: 1 }}
                helperText="When prompt is limited, include up to this many photos per cluster"
                disabled={config.maxPhotosInPrompt === 'all' && !config.useClusterSamplingEvenWhenUnlimited}
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </TextField>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Prompt Size Controls</Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.compactAiDescriptionInPrompt}
                    onChange={(e) => handleConfigChange('compactAiDescriptionInPrompt', e.target.checked)}
                  />
                }
                label="Compact AI Description in prompt (recommended)"
              />

              <TextField
                select
                fullWidth
                size="small"
                label="AI Description max chars (when compacting)"
                value={config.aiDescriptionMaxChars}
                onChange={(e) => handleConfigChange('aiDescriptionMaxChars', Number(e.target.value))}
                sx={{ mt: 1 }}
                helperText="Smaller = fewer tokens"
                disabled={!config.compactAiDescriptionInPrompt}
              >
                {[120, 180, 260, 400, 800].map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </TextField>
            </Box>

            <Button
              variant="contained"
              size="large"
              startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
              onClick={handleGenerateAlbum}
              disabled={generating || !albumData}
              fullWidth
            >
              {generating ? 'Generating Album...' : 'Generate Album with AI'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* AI Recommendations */}
      {recommendations && (
        <>
          <Card sx={{ mb: 4, bgcolor: 'success.light', color: 'success.contrastText' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <AutoAwesomeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                AI Recommendations Generated
              </Typography>
              {recommendations.loadedFromSession && (
                <Typography variant="body2">
                  Source: Session (persisted){recommendations.generatedAt ? ` — saved at ${new Date(recommendations.generatedAt).toLocaleString()}` : ''}
                </Typography>
              )}
              <Typography variant="body2">
                Model: {recommendations.aiModel} | Tokens Used: {recommendations.tokensUsed}
              </Typography>
              {recommendations.loadedFromSession && (
                <Typography variant="body2">
                  Stored text truncated: prompt {recommendations.storedApiPromptTextTruncated ? 'Yes' : 'No'}; response {recommendations.storedModelResponseTextTruncated ? 'Yes' : 'No'}
                </Typography>
              )}
              {recommendations.tokenUsage && (
                <Typography variant="body2">
                  Actual tokens: {recommendations.tokenUsage.prompt_tokens} input + {recommendations.tokenUsage.completion_tokens} output = {recommendations.tokenUsage.total_tokens}
                </Typography>
              )}
              {typeof recommendations.modelResponseTimeMs === 'number' && (
                <Typography variant="body2">
                  Model response time: {(recommendations.modelResponseTimeMs / 1000).toFixed(2)}s ({recommendations.modelResponseTimeMs} ms)
                </Typography>
              )}
              {recommendations.tokenEstimation && (
                <Typography variant="body2">
                  Estimated tokens: {recommendations.tokenEstimation.estimatedPromptTokens} input + {recommendations.tokenEstimation.estimatedResponseTokens} output = {recommendations.tokenEstimation.estimatedTotalTokens}
                </Typography>
              )}
              {recommendations.costEstimationUSD && (
                <Typography variant="body2">
                  Estimated cost (USD): ${recommendations.costEstimationUSD.totalUSD} (input ${recommendations.costEstimationUSD.inputUSD} + output ${recommendations.costEstimationUSD.outputUSD})
                </Typography>
              )}
              {recommendations.actualCostEstimationUSD && (
                <Typography variant="body2">
                  Actual-token cost estimate (USD): ${recommendations.actualCostEstimationUSD.totalUSD} (input ${recommendations.actualCostEstimationUSD.inputUSD} + output ${recommendations.actualCostEstimationUSD.outputUSD})
                </Typography>
              )}
              {recommendations.promptBreakdown?.api && (
                <Typography variant="body2">
                  Prompt breakdown: {recommendations.promptBreakdown.api.photosIncluded} photos, avg {recommendations.promptBreakdown.api.avgCharsPerPhoto} chars/photo
                </Typography>
              )}
              {recommendations.apiPromptFilePath && (
                <Typography variant="body2">
                  API prompt file (actual sent to model): {recommendations.apiPromptFilePath}
                </Typography>
              )}
              {recommendations.promptFilePath && (
                <Typography variant="body2">
                  Full prompt file (all photos): {recommendations.promptFilePath}
                </Typography>
              )}
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleLoadGenerationArtifacts}
                  disabled={loadingArtifacts}
                >
                  {loadingArtifacts ? 'Loading DB prompt/response...' : 'Load full prompt/response from DB'}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {generationArtifacts?.albumGeneration && (
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Persisted Prompt & Response (DB)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Full prompt chars: {generationArtifacts.lengths?.fullPromptChars ?? 0} | API prompt chars: {generationArtifacts.lengths?.apiPromptChars ?? 0} | Response chars: {generationArtifacts.lengths?.modelResponseChars ?? 0}
                </Typography>

                <Typography variant="subtitle2" sx={{ mt: 2 }}>Full Prompt (all photos)</Typography>
                <Paper variant="outlined" sx={{ p: 1, maxHeight: 240, overflow: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
                  {generationArtifacts.albumGeneration.lastFullPromptText || 'No full prompt text saved'}
                </Paper>

                <Typography variant="subtitle2" sx={{ mt: 2 }}>API Prompt (actual sent)</Typography>
                <Paper variant="outlined" sx={{ p: 1, maxHeight: 240, overflow: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
                  {generationArtifacts.albumGeneration.lastApiPromptText || 'No API prompt text saved'}
                </Paper>

                <Typography variant="subtitle2" sx={{ mt: 2 }}>Model Response (raw)</Typography>
                <Paper variant="outlined" sx={{ p: 1, maxHeight: 240, overflow: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
                  {generationArtifacts.albumGeneration.lastModelResponseText || 'No model response text saved'}
                </Paper>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Album Title and Description */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                {recommendations.recommendations.albumTitle}
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {recommendations.recommendations.albumDescription}
              </Typography>
            </CardContent>
          </Card>

          {/* Album Preview */}
          <Accordion sx={{ mb: 2 }} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Album Preview (Pages)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {!(recommendations.recommendations?.pages || []).length && (
                <Alert severity="info">No pages returned by the model.</Alert>
              )}

              <Stack spacing={2}>
                {(recommendations.recommendations?.pages || []).map((page) => {
                  const imageIds = Array.isArray(page.imageIds) ? page.imageIds : [];
                  const layout = page.layout || 'multi';
                  const gridXs = layout === 'single' ? 12 : (layout === 'double' ? 6 : (layout === 'quad' ? 6 : 4));
                  const imageHeight = layout === 'single' ? 260 : (layout === 'double' ? 200 : (layout === 'quad' ? 170 : 120));
                  return (
                    <Paper key={page.pageNumber} variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Page {page.pageNumber} — {page.layout}{page.pageTitle ? ` — ${page.pageTitle}` : ''}
                      </Typography>
                      {page.reasoning && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {page.reasoning}
                        </Typography>
                      )}

                      <Grid container spacing={1}>
                        {imageIds.map((imageId) => {
                          const image = (detailedAlbumData?.images || []).find((img) => img.id === imageId);
                          return (
                            <Grid item xs={gridXs} sm={gridXs} md={gridXs} key={imageId}>
                              <Paper variant="outlined" sx={{ p: 1 }}>
                                <AuthenticatedImage
                                  imageId={imageId}
                                  type="thumbnail"
                                  fallbackToServe
                                  alt={image?.name || imageId}
                                  sx={{ width: '100%', height: imageHeight, objectFit: 'cover', borderRadius: 1 }}
                                />
                                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                  {image?.name || imageId}
                                </Typography>
                              </Paper>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Paper>
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Input Data Summary */}
          <Accordion sx={{ mb: 2 }} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Input Data Sent to AI (Detailed)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Paper variant="outlined" sx={{ p: 2 }}>
                {!detailedAlbumData && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Detailed album data is not available in the current response.
                  </Alert>
                )}

                {/* Session Information */}
                <Typography variant="h6" gutterBottom>Session Information</Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2"><strong>Session Name:</strong> {detailedAlbumData?.sessionName || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Session Type:</strong> {detailedAlbumData?.sessionEventType || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Description:</strong> {detailedAlbumData?.sessionDescription || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Total Images:</strong> {detailedAlbumData?.stats?.totalImages ?? 0}</Typography>
                <Typography variant="body2"><strong>Total Clusters:</strong> {detailedAlbumData?.stats?.totalClusters ?? 0}</Typography>
                <Typography variant="body2"><strong>Images with Faces:</strong> {detailedAlbumData?.stats?.imagesWithFaces ?? 0}</Typography>
                <Typography variant="body2"><strong>People Detected:</strong> {(detailedAlbumData?.people || []).join(', ') || 'None'}</Typography>
                
                {/* Album Configuration */}
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Album Configuration</Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2"><strong>Total Pages:</strong> {recommendations.albumConfiguration?.totalPages ?? 'N/A'}</Typography>
                <Typography variant="body2"><strong>Layout Distribution:</strong></Typography>
                <Box sx={{ ml: 2 }}>
                  <Typography variant="body2">• Single image pages: {recommendations.albumConfiguration?.imagesPerPage?.single ?? 'N/A'}</Typography>
                  <Typography variant="body2">• Double image pages: {recommendations.albumConfiguration?.imagesPerPage?.double ?? 'N/A'}</Typography>
                  <Typography variant="body2">• Quad (4 images) pages: {recommendations.albumConfiguration?.imagesPerPage?.quad ?? 'N/A'}</Typography>
                  <Typography variant="body2">• Multi (6-8 images) pages: {recommendations.albumConfiguration?.imagesPerPage?.multi ?? 'N/A'}</Typography>
                </Box>
                <Typography variant="body2"><strong>User Context:</strong> {recommendations.albumConfiguration?.userContext || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Album Theme:</strong> {recommendations.albumConfiguration?.albumTheme || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Prioritize Timeline:</strong> {recommendations.albumConfiguration?.prioritizeTimeline ? 'Yes' : 'No'}</Typography>
                <Typography variant="body2"><strong>Prioritize People:</strong> {recommendations.albumConfiguration?.prioritizePeople ? 'Yes' : 'No'}</Typography>
                <Typography variant="body2"><strong>Use Clustering:</strong> {recommendations.albumConfiguration?.prioritizeClusters ? 'Yes' : 'No'}</Typography>
                <Typography variant="body2"><strong>Max Photos In Prompt:</strong> {recommendations.albumConfiguration?.maxPhotosInPrompt === 'all' ? 'All' : (recommendations.albumConfiguration?.maxPhotosInPrompt ?? 'N/A')}</Typography>
                <Typography variant="body2"><strong>Photos per cluster (sampling):</strong> {recommendations.albumConfiguration?.clusterSampleSize ?? 'N/A'}</Typography>
                <Typography variant="body2"><strong>Sampling when unlimited:</strong> {recommendations.albumConfiguration?.useClusterSamplingEvenWhenUnlimited ? 'Yes' : 'No'}</Typography>
                <Typography variant="body2"><strong>Compact AI Description:</strong> {recommendations.albumConfiguration?.compactAiDescriptionInPrompt ? 'Yes' : 'No'}</Typography>
                <Typography variant="body2"><strong>AI Description max chars:</strong> {recommendations.albumConfiguration?.aiDescriptionMaxChars ?? 'N/A'}</Typography>

                {/* Prompt Breakdown */}
                {recommendations.promptBreakdown?.api && (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Prompt Size Breakdown (API Prompt)</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2"><strong>Total chars:</strong> {recommendations.promptBreakdown.api.totalChars}</Typography>
                    <Typography variant="body2"><strong>Total estimated tokens:</strong> {recommendations.promptBreakdown.api.totalTokens}</Typography>
                    <Typography variant="body2"><strong>Photos included:</strong> {recommendations.promptBreakdown.api.photosIncluded}</Typography>
                    <Typography variant="body2"><strong>Avg chars/photo:</strong> {recommendations.promptBreakdown.api.avgCharsPerPhoto}</Typography>
                    <Box sx={{ ml: 2, mt: 1 }}>
                      {Object.entries(recommendations.promptBreakdown.api.sections || {}).map(([sectionKey, data]) => (
                        <Typography key={sectionKey} variant="body2">
                          • {sectionKey}: {data.chars} chars (~{data.tokens} tokens)
                        </Typography>
                      ))}
                    </Box>
                  </>
                )}

                {/* Clusters Detail */}
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Clusters ({detailedAlbumData?.clusters?.length || 0})</Typography>
                <Divider sx={{ mb: 2 }} />
                {(detailedAlbumData?.clusters || []).map((cluster, idx) => (
                  <Accordion key={cluster.id} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">
                        {cluster.label} ({cluster.imageIds.length} images)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                        <strong>Cluster ID:</strong> {cluster.id}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                        <strong>Image IDs:</strong> {cluster.imageIds.join(', ')}
                      </Typography>
                      <Stack spacing={1}>
                        {cluster.imageIds.map(imageId => {
                          const image = (detailedAlbumData?.images || []).find(img => img.id === imageId);
                          return (
                            <Paper key={imageId} variant="outlined" sx={{ p: 1 }}>
                              <Typography variant="body2"><strong>Image:</strong> {image?.name || imageId}</Typography>
                              <Typography variant="caption" display="block">
                                <strong>ID:</strong> {imageId}
                              </Typography>
                              {image?.captureDate && (
                                <Typography variant="caption" display="block">
                                  <strong>Date:</strong> {new Date(image.captureDate).toLocaleString()}
                                </Typography>
                              )}
                              <Typography variant="caption" display="block">
                                <strong>AI Description:</strong> {image?.embeddingText || image?.parsedDescription?.description || image?.parsedDescription?.summary || 'N/A (no embedding description found in DB)'}
                              </Typography>
                              {image?.sceneType && (
                                <Typography variant="caption" display="block">
                                  <strong>Scene:</strong> {image.sceneType} - {image.sceneDetails}
                                </Typography>
                              )}
                            </Paper>
                          );
                        })}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))}

                {/* All Images Detail */}
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>All Images ({detailedAlbumData?.images?.length || 0})</Typography>
                <Divider sx={{ mb: 2 }} />
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">
                      Click to view all images with full details
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      {(detailedAlbumData?.images || []).map((image, idx) => (
                        <Paper key={image.id} variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Photo {idx + 1}: {image.name}
                          </Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" display="block">
                                <strong>ID:</strong> {image.id}
                              </Typography>
                              <Typography variant="caption" display="block">
                                <strong>Date:</strong> {image.captureDate ? new Date(image.captureDate).toLocaleString() : 'Unknown'}
                              </Typography>
                              {image.dimensions && (
                                <Typography variant="caption" display="block">
                                  <strong>Dimensions:</strong> {image.dimensions.width}x{image.dimensions.height}
                                </Typography>
                              )}
                              {image.gpsLocation && (
                                <Typography variant="caption" display="block">
                                  <strong>GPS:</strong> {image.gpsLocation.latitude.toFixed(5)}, {image.gpsLocation.longitude.toFixed(5)}
                                </Typography>
                              )}
                              {image.cameraInfo && (
                                <Typography variant="caption" display="block">
                                  <strong>Camera:</strong> {image.cameraInfo}
                                </Typography>
                              )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" display="block">
                                <strong>Scene Type:</strong> {image.sceneType || 'Unknown'}
                              </Typography>
                              {image.sceneDetails && (
                                <Typography variant="caption" display="block">
                                  <strong>Scene Details:</strong> {image.sceneDetails}
                                </Typography>
                              )}
                              {image.timeContext && (
                                <Typography variant="caption" display="block">
                                  <strong>Time Context:</strong> {image.timeContext}
                                </Typography>
                              )}
                              {image.locationType && (
                                <Typography variant="caption" display="block">
                                  <strong>Location Type:</strong> {image.locationType}
                                </Typography>
                              )}
                              {image.socialContext && (
                                <Typography variant="caption" display="block">
                                  <strong>Social Context:</strong> {image.socialContext}
                                </Typography>
                              )}
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                <strong>AI Description:</strong> {image.embeddingText || image.parsedDescription?.description || image.parsedDescription?.summary || 'N/A (no embedding description found in DB)'}
                              </Typography>
                              {image.sceneTags && image.sceneTags.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" display="block">
                                    <strong>Tags:</strong>
                                  </Typography>
                                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {image.sceneTags.map((tag, tagIdx) => (
                                      <Chip key={tagIdx} label={tag} size="small" variant="outlined" />
                                    ))}
                                  </Stack>
                                </Box>
                              )}
                              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                <strong>Cluster:</strong> {image.clusterLabel || 'Unclustered'}
                              </Typography>
                              
                              {/* Face Recognition Data */}
                              {image.facesDetected > 0 && (
                                <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                  <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                                    Faces Detected: {image.facesDetected}
                                  </Typography>
                                  {image.faceMatches && image.faceMatches.length > 0 && (
                                    <Box sx={{ ml: 1, mt: 0.5 }}>
                                      {image.faceMatches.map((face, faceIdx) => (
                                        <Box key={faceIdx} sx={{ mb: 0.5 }}>
                                          <Typography variant="caption" display="block">
                                            <strong>Person {faceIdx + 1}:</strong> {face.personName}
                                          </Typography>
                                          <Typography variant="caption" display="block" sx={{ ml: 1 }}>
                                            Confidence: {face.confidence ? face.confidence.toFixed(2) : 'N/A'}%
                                          </Typography>
                                          {face.faceBox && (
                                            <Typography variant="caption" display="block" sx={{ ml: 1 }}>
                                              Position: ({face.faceBox.Left?.toFixed(2)}, {face.faceBox.Top?.toFixed(2)}) 
                                              Size: {face.faceBox.Width?.toFixed(2)}x{face.faceBox.Height?.toFixed(2)}
                                            </Typography>
                                          )}
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Paper>
            </AccordionDetails>
          </Accordion>

          {/* Pages */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Album Pages</Typography>
              <Divider sx={{ mb: 2 }} />
              
              {recommendations.recommendations.pages && recommendations.recommendations.pages.map((page, index) => (
                <Accordion key={index}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1">
                      Page {page.pageNumber}: {String(page.layout || 'unknown').toUpperCase()} Layout
                      {page.pageTitle && ` - ${page.pageTitle}`}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      <strong>Reasoning:</strong> {page.reasoning}
                    </Typography>
                    <Typography variant="body2" paragraph>
                      <strong>Images ({Array.isArray(page.imageIds) ? page.imageIds.length : 0}):</strong>
                    </Typography>
                    <Stack spacing={1}>
                      {(Array.isArray(page.imageIds) ? page.imageIds : []).map(imageId => {
                        const image = (albumData?.images || []).find(img => img.id === imageId);
                        return (
                          <Paper key={imageId} variant="outlined" sx={{ p: 1 }}>
                            <Typography variant="body2">
                              <strong>{image?.name || imageId}</strong>
                            </Typography>
                            {image?.embeddingText && (
                              <Typography variant="caption" color="text.secondary">
                                {image.embeddingText}
                              </Typography>
                            )}
                          </Paper>
                        );
                      })}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}

              {/* Summary */}
              {recommendations.recommendations.summary && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="subtitle1" gutterBottom>Summary</Typography>
                  <Typography variant="body2">
                    <strong>Total Photos Used:</strong> {recommendations.recommendations.summary.totalPhotosUsed}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Photos Not Included:</strong> {recommendations.recommendations.summary.photosNotIncluded}
                  </Typography>
                  
                  {recommendations.recommendations.summary.keyMoments && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 2 }}>Key Moments:</Typography>
                      <ul>
                        {recommendations.recommendations.summary.keyMoments.map((moment, idx) => (
                          <li key={idx}>
                            <Typography variant="body2">{moment}</Typography>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {recommendations.recommendations.summary.suggestions && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 2 }}>Suggestions:</Typography>
                      <ul>
                        {recommendations.recommendations.summary.suggestions.map((suggestion, idx) => (
                          <li key={idx}>
                            <Typography variant="body2">{suggestion}</Typography>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setRecommendations(null)}
            >
              Generate New Recommendations
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                // Future: Navigate to preview page
                alert('Preview feature coming soon!');
              }}
            >
              Preview Album
            </Button>
          </Box>
        </>
      )}

      {/* Configuration Dialog */}
      <Dialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Album Configuration</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Total Pages</Typography>
            <Slider
              value={config.totalPages}
              onChange={(e, value) => handleConfigChange('totalPages', value)}
              min={10}
              max={100}
              step={5}
              marks
              valueLabelDisplay="on"
              sx={{ mb: 3 }}
            />

            <Typography variant="subtitle2" gutterBottom>Page Layout Distribution</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Total must equal {config.totalPages} pages
            </Typography>

            <TextField
              fullWidth
              label="Single Image Pages"
              type="number"
              value={config.imagesPerPage.single}
              onChange={(e) => handleImagesPerPageChange('single', parseInt(e.target.value))}
              sx={{ mb: 2 }}
              inputProps={{ min: 0 }}
            />

            <TextField
              fullWidth
              label="Double Image Pages"
              type="number"
              value={config.imagesPerPage.double}
              onChange={(e) => handleImagesPerPageChange('double', parseInt(e.target.value))}
              sx={{ mb: 2 }}
              inputProps={{ min: 0 }}
            />

            <TextField
              fullWidth
              label="Quad (4 Images) Pages"
              type="number"
              value={config.imagesPerPage.quad}
              onChange={(e) => handleImagesPerPageChange('quad', parseInt(e.target.value))}
              sx={{ mb: 2 }}
              inputProps={{ min: 0 }}
            />

            <TextField
              fullWidth
              label="Multi (6-8 Images) Pages"
              type="number"
              value={config.imagesPerPage.multi}
              onChange={(e) => handleImagesPerPageChange('multi', parseInt(e.target.value))}
              sx={{ mb: 2 }}
              inputProps={{ min: 0 }}
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              Current total: {config.imagesPerPage.single + config.imagesPerPage.double + 
                            config.imagesPerPage.quad + config.imagesPerPage.multi} pages
              <br />
              Estimated images in album: {
                config.imagesPerPage.single * 1 +
                config.imagesPerPage.double * 2 +
                config.imagesPerPage.quad * 4 +
                config.imagesPerPage.multi * 7
              } out of {albumData?.stats?.totalImages || 0} total images in session
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AlbumCreationPage;
